import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  MoreVert,
  CloudUpload,
  CreateNewFolder,
  Link as LinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DriveFileRenameOutline as RenameIcon,
  Home as HomeIcon,
  OpenInNew,
} from '@mui/icons-material'
import api from '../api/client'
import { useAuthStore } from '../stores/authStore'
import { usePortalStore } from '../stores/portalStore'
import { useDownloadsStore } from '../stores/downloadsStore'
import FileIcon from '../components/FileIcon'
import UploadDialog, { type UploadItem } from '../components/UploadDialog'

interface DownloadItem {
  name: string
  path: string
  type: string
  size: number
  modified: string
  mime_type?: string
  title?: string
  description?: string
  url?: string
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    })
  } catch {
    return iso
  }
}

export default function DownloadsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const { config } = usePortalStore()
  const { currentPath, setCurrentPath } = useDownloadsStore()

  const glassmorphism = (config?.glassmorphism ?? false) && config?.backgroundMode === 'image'
  const glassSx = glassmorphism
    ? { bgcolor: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }
    : {}

  const [uploadOpen, setUploadOpen] = useState(false)
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [newLinkOpen, setNewLinkOpen] = useState(false)
  const [newLinkTitle, setNewLinkTitle] = useState('')
  const [newLinkUrl, setNewLinkUrl] = useState('')
  const [newLinkDesc, setNewLinkDesc] = useState('')
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameItem, setRenameItem] = useState<DownloadItem | null>(null)
  const [renameName, setRenameName] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<DownloadItem | null>(null)
  const [metaOpen, setMetaOpen] = useState(false)
  const [metaItem, setMetaItem] = useState<DownloadItem | null>(null)
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDescription, setMetaDescription] = useState('')
  const [metaUrl, setMetaUrl] = useState('')
  const [actionError, setActionError] = useState('')
  const [menuAnchor, setMenuAnchor] = useState<null | { el: HTMLElement; item: DownloadItem }>(null)

  const { data: fileList, isLoading } = useQuery<{ items: DownloadItem[]; path: string }>({
    queryKey: ['downloads', 'list', currentPath],
    queryFn: async () => {
      const res = await api.get('/downloads/list', { params: { path: currentPath } })
      return res.data
    },
  })

  const mkdirMutation = useMutation({
    mutationFn: async (name: string) => {
      await api.post('/downloads/mkdir', null, { params: { path: currentPath, name } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloads', 'list'] })
      setNewFolderOpen(false)
      setNewFolderName('')
    },
    onError: () => setActionError(t('common.error')),
  })

  const createLinkMutation = useMutation({
    mutationFn: async ({ title, url, description }: { title: string; url: string; description: string }) => {
      await api.post('/downloads/link', null, { params: { path: currentPath, title, url, description } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloads', 'list'] })
      setNewLinkOpen(false)
      setNewLinkTitle('')
      setNewLinkUrl('')
      setNewLinkDesc('')
    },
    onError: () => setActionError(t('common.error')),
  })

  const renameMutation = useMutation({
    mutationFn: async ({ item, newName }: { item: DownloadItem; newName: string }) => {
      await api.post('/downloads/rename', null, { params: { path: item.path, newName } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloads', 'list'] })
      setRenameOpen(false)
      setRenameItem(null)
    },
    onError: () => setActionError(t('common.error')),
  })

  const deleteMutation = useMutation({
    mutationFn: async (paths: string[]) => {
      await api.post('/downloads/delete', paths)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloads', 'list'] })
      setDeleteConfirm(null)
    },
    onError: () => setActionError(t('common.error')),
  })

  const metaMutation = useMutation({
    mutationFn: async ({ path, filename, title, description, url }: { path: string; filename: string; title: string; description: string; url: string }) => {
      await api.put('/downloads/meta', null, { params: { path, filename, title, description, url } })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloads', 'list'] })
      setMetaOpen(false)
      setMetaItem(null)
    },
    onError: () => setActionError(t('common.error')),
  })

  const handleUpload = useCallback(async (items: UploadItem[]) => {
    const formData = new FormData()
    for (const item of items) {
      formData.append('files', item.file, item.relativePath || item.file.name)
    }
    await api.post('/downloads/upload', formData, {
      params: { path: currentPath },
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    queryClient.invalidateQueries({ queryKey: ['downloads', 'list'] })
    setUploadOpen(false)
  }, [currentPath, queryClient])

  const handleDownload = useCallback((item: DownloadItem) => {
    if (item.type === 'link' && item.url) {
      window.open(item.url, '_blank', 'noopener')
    } else {
      window.open(`${api.defaults.baseURL}/downloads/download?path=${encodeURIComponent(item.path)}`, '_blank')
    }
  }, [])

  const handleNavigate = useCallback((path: string) => {
    setCurrentPath(path)
  }, [setCurrentPath])

  const breadcrumbs = currentPath === '/'
    ? []
    : currentPath.split('/').filter(Boolean)

  const openRename = (item: DownloadItem) => {
    setRenameItem(item)
    setRenameName(item.name)
    setRenameOpen(true)
    setMenuAnchor(null)
  }

  const openDelete = (item: DownloadItem) => {
    setDeleteConfirm(item)
    setMenuAnchor(null)
  }

  const openMeta = (item: DownloadItem) => {
    setMetaItem(item)
    setMetaTitle(item.title || '')
    setMetaDescription(item.description || '')
    setMetaUrl(item.url || '')
    setMetaOpen(true)
    setMenuAnchor(null)
  }

  const items = fileList?.items ?? []

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" component="h2">
          {t('downloads.title')}
        </Typography>

        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUpload />}
              onClick={() => setUploadOpen(true)}
              sx={glassSx}
            >
              {t('downloads.upload')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreateNewFolder />}
              onClick={() => setNewFolderOpen(true)}
              sx={glassSx}
            >
              {t('downloads.newFolder')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => setNewLinkOpen(true)}
              sx={glassSx}
            >
              {t('downloads.newLink')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3, ...glassSx, borderRadius: 1, px: 1, py: 0.5, width: 'fit-content' }}>
        <Link
          underline="hover"
          color={currentPath === '/' ? 'text.primary' : 'primary'}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
          onClick={() => handleNavigate('/')}
        >
          <HomeIcon fontSize="small" />
          {t('downloads.root')}
        </Link>
        {breadcrumbs.map((segment, idx) => {
          const segPath = '/' + breadcrumbs.slice(0, idx + 1).join('/')
          const isLast = idx === breadcrumbs.length - 1
          return (
            <Link
              key={segPath}
              underline="hover"
              color={isLast ? 'text.primary' : 'primary'}
              sx={{ cursor: 'pointer' }}
              onClick={() => handleNavigate(segPath)}
            >
              {segment}
            </Link>
          )
        })}
      </Breadcrumbs>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Alert severity="info">{t('downloads.empty')}</Alert>
      ) : (
        <Grid container spacing={2}>
          {items.map((item) => {
            const ext = item.name.includes('.') ? item.name.split('.').pop()! : ''
            const displayName = item.title || item.name
            const isFolder = item.type === 'directory'
            const isLink = item.type === 'link'

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.path}>
                <Tooltip title={item.description || displayName} arrow placement="top">
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 6,
                        bgcolor: glassmorphism ? 'rgba(255,255,255,0.85)' : undefined,
                      },
                      position: 'relative',
                      ...glassSx,
                      border: glassmorphism ? '1px solid rgba(255,255,255,0.35)' : undefined,
                    }}
                    onClick={() => {
                      if (isFolder) {
                        handleNavigate(item.path)
                      } else {
                        handleDownload(item)
                      }
                    }}
                  >
                    {/* Admin menu button */}
                    {isAdmin && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuAnchor({ el: e.currentTarget, item })
                        }}
                        sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    )}

                    <CardContent sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
                      {isLink ? (
                        <LinkIcon sx={{ fontSize: 56, color: 'secondary.main' }} />
                      ) : (
                        <FileIcon type={item.type} extension={ext} size={56} />
                      )}

                      <Typography
                        variant="subtitle1"
                        component="div"
                        sx={{
                          mt: 1.5,
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        {displayName}
                        {isLink && <OpenInNew sx={{ fontSize: 14, color: 'text.secondary' }} />}
                      </Typography>

                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}

                      {!isFolder && !isLink && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {formatSize(item.size)} · {formatDate(item.modified)}
                        </Typography>
                      )}

                      {isLink && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {item.url}
                        </Typography>
                      )}

                      {isFolder && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {formatDate(item.modified)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            )
          })}
        </Grid>
      )}

      {/* Context menu for admin */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => menuAnchor && openRename(menuAnchor.item)}>
          <RenameIcon sx={{ mr: 1 }} fontSize="small" />
          {t('downloads.rename')}
        </MenuItem>
        <MenuItem onClick={() => menuAnchor && openMeta(menuAnchor.item)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          {t('downloads.editDescription')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuAnchor && openDelete(menuAnchor.item)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          {t('downloads.delete')}
        </MenuItem>
      </Menu>

      {/* Upload dialog */}
      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
      />

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('downloads.newFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('downloads.folderName')}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => mkdirMutation.mutate(newFolderName)}
            disabled={!newFolderName.trim()}
          >
            {t('downloads.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New link dialog */}
      <Dialog open={newLinkOpen} onClose={() => setNewLinkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('downloads.newLink')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('downloads.titleLabel')}
            value={newLinkTitle}
            onChange={(e) => setNewLinkTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('downloads.urlLabel')}
            placeholder="https://..."
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('downloads.descriptionLabel')}
            value={newLinkDesc}
            onChange={(e) => setNewLinkDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewLinkOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => createLinkMutation.mutate({ title: newLinkTitle, url: newLinkUrl, description: newLinkDesc })}
            disabled={!newLinkTitle.trim() || !newLinkUrl.trim()}
          >
            {t('downloads.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('downloads.rename')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('downloads.rename')}
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => renameItem && renameMutation.mutate({ item: renameItem, newName: renameName })}
            disabled={!renameName.trim()}
          >
            {t('downloads.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('downloads.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('downloads.confirmDelete')}</Typography>
          {deleteConfirm && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {deleteConfirm.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirm && deleteMutation.mutate([deleteConfirm.path])}
          >
            {t('downloads.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit description dialog */}
      <Dialog open={metaOpen} onClose={() => setMetaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('downloads.editDescription')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('downloads.titleLabel')}
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          {metaItem?.type === 'link' && (
            <TextField
              fullWidth
              label={t('downloads.urlLabel')}
              value={metaUrl}
              onChange={(e) => setMetaUrl(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('downloads.descriptionLabel')}
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetaOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (metaItem) {
                const dirPath = metaItem.path.substring(0, metaItem.path.lastIndexOf('/')) || '/'
                metaMutation.mutate({
                  path: dirPath,
                  filename: metaItem.name,
                  title: metaTitle,
                  description: metaDescription,
                  url: metaUrl,
                })
              }
            }}
          >
            {t('downloads.saveDescription')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}